(() => {
  if (customElements.get('sam-product-stories')) return;
  class SamProductStories extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.track = this.querySelector('.sam-product-stories__cards');
      this.cards = [...this.querySelectorAll('.sam-product-stories__card')];
      this.dots = [...this.querySelectorAll('[data-story-index]')];
      this.pause = this.querySelector('[data-story-pause]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.targetIndex = 0;
      this.paused = this.motion.matches;
      if (window.Shopify?.designMode) this.paused = true;
      if (this.cards.length < 2) return;
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.cards.indexOf(event.target.closest('.sam-product-stories__card'));
        if (index >= 0) { this.paused = true; this.goTo(index); this.schedule(); }
      }, { signal });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-story-prev')) this.goTo(this.index - 1);
        if (button.hasAttribute('data-story-next')) this.goTo(this.index + 1);
        if (button.hasAttribute('data-story-index')) this.goTo(Number(button.dataset.storyIndex));
        if (button === this.pause) { this.paused = !this.paused; this.updatePause(); }
        this.schedule();
      }, { signal });
      this.track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        this.schedule();
      }, { signal });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
        this.schedule();
      }, { passive: true, signal });
      this.addEventListener('pointerdown', () => { this.targetIndex = null; this.touching = true; this.schedule(); }, { signal });
      this.track.addEventListener('wheel', () => { this.targetIndex = null; }, { passive: true, signal });
      const release = () => { this.touching = false; this.schedule(); };
      window.addEventListener('pointerup', release, { signal });
      window.addEventListener('pointercancel', release, { signal });
      this.addEventListener('pointerenter', (event) => { this.hovering = event.pointerType === 'mouse'; this.schedule(); }, { signal });
      this.addEventListener('pointerleave', () => { this.hovering = false; this.schedule(); }, { signal });
      this.addEventListener('focusin', () => this.schedule(), { signal });
      this.addEventListener('focusout', () => this.schedule(), { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.motion.addEventListener('change', () => { this.paused = this.motion.matches; this.updatePause(); this.schedule(); }, { signal });
      this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.schedule(); }, { threshold: .25 });
      this.observer.observe(this.track);
      this.resize = new ResizeObserver(() => { this.targetIndex = null; this.update(); });
      this.resize.observe(this.track);
      this.classList.add('is-enhanced');
      this.updatePause();
      this.update();
    }
    schedule() {
      clearTimeout(this.timer);
      if (window.Shopify?.designMode || this.paused || this.motion.matches || !this.visible || document.hidden || this.touching || this.hovering) return;
      this.timer = setTimeout(() => {
        if (this.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) return;
        this.goTo(this.index + 1);
        this.schedule();
      }, 2500);
    }
    goTo(index) {
      const target = (index + this.cards.length) % this.cards.length;
      this.targetIndex = target;
      this.index = target;
      this.update();
      const card = this.cards[target].getBoundingClientRect();
      const track = this.track.getBoundingClientRect();
      this.track.scrollTo({ left: this.track.scrollLeft + card.left - track.left - (track.width - card.width) / 2, behavior: this.motion.matches ? 'auto' : 'smooth' });
    }
    update() {
      const track = this.track.getBoundingClientRect();
      const center = track.left + track.width / 2;
      this.index = this.targetIndex ?? this.cards.reduce((best, card, index) => {
        const rect = card.getBoundingClientRect();
        const previous = this.cards[best].getBoundingClientRect();
        return Math.abs(rect.left + rect.width / 2 - center) < Math.abs(previous.left + previous.width / 2 - center) ? index : best;
      }, 0);
      this.dots.forEach((dot, index) => {
        if (index === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
    updatePause() {
      this.pause?.setAttribute('aria-pressed', String(this.paused));
      this.pause?.setAttribute('aria-label', this.paused ? 'Play automatic testimonials' : 'Pause automatic testimonials');
      this.querySelector('[data-pause-icon]')?.setAttribute('d', this.paused ? 'm8 5 11 7-11 7V5Z' : 'M8 5v14M16 5v14');
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
    }
  }
  customElements.define('sam-product-stories', SamProductStories);
})();
